import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { API } from 'constants';
import client from '../../../services/cycle-api';

import {
    CONTENT_DETAIL_KEY,
    CONTENT_GROUP_DETAIL_KEY,
    CONTENT_GROUP_TYPE,
    CONTENT_GROUP_TYPES_WITH_EDITING,
} from '../../../constants/cycleDetail';
import { SETTING_ACTION_TYPE } from '../../../constants/settingsForm';
import { defaultErrorMessage } from '../../../constants/errorMessages';

import {
    replaceFilesInFormData,
    getInitialSettingsFormData,
    formatSettingsForRequestBody,
} from '../../../helpers/settingsFormUtils';
import { checkIfFormDataChanged } from '../../../helpers/checkIfFormDataChanged';
import { sortCycleBackendResponse, sortStageContent } from '../../../helpers/cycleSortUtils';
import { checkIfReadyToSaveChanges, checkIfReadyToSubmit } from '../../../helpers/cycleFormUtils';

import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { useResponsiveBreakpoints } from '../../../hooks/useResponsiveBreakpoints';
import { useHandlePageDataLoadError } from '../../../hooks/useHandlePageDataLoadError';

import Loading from '../../../components/Loading';
import ErrorAlert from '../../../design-system/ErrorAlert/ErrorAlert';
import CycleMainPanel from './CycleMainPanel/CycleMainPanel';
import CycleSidePanel from './CycleSidePanel/CycleSidePanel';

const CraftCycleDetailPage = () => {
    const { cycleId } = useParams();

    const navigate = useNavigate();
    const location = useLocation();

    const backLinkHref = location.state?.from || '/cycles';

    const [cycleData, setCycleData] = useState(null);

    const [formData, setFormData] = useState(null);

    const originalFormDataRef = useRef(null);
    const [areChangesToSave, setAreChangesToSave] = useState(false);
    const [actionsLoading, setActionsLoading] = useState(null);

    const [expandedGroups, setExpandedGroups] = useState({});

    const [errorAlert, setErrorAlert] = useState(null);

    const { handlePageDataLoadError } = useHandlePageDataLoadError();

    const { isDesktop } = useResponsiveBreakpoints();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data } = await client.get(API.ROUTES.cycle.cycle + cycleId + '/');

                const sortedData = sortCycleBackendResponse(data);
                const detailsFormData = getInitialStageDetailsFormData(sortedData.current_stage);

                setFormData(detailsFormData);
                originalFormDataRef.current = detailsFormData;

                setCycleData(sortedData);
            } catch (e) {
                handlePageDataLoadError({
                    e,
                    redirectPath: '/cycles',
                    generalErrorHandler: () => navigate(backLinkHref),
                });
            }
        };

        void fetchData();
    }, []);

    useEffect(() => {
        const originalFormData = originalFormDataRef.current;
        const isFormDataChanged = checkIfFormDataChanged(originalFormData, formData);

        if (isFormDataChanged !== areChangesToSave) {
            setAreChangesToSave(isFormDataChanged);
        }
    }, [formData]);

    useDocumentTitle(cycleData?.name);

    const getInitialStageDetailsFormData = (stage) => {
        if (!stage) return {};

        const stageStatus = stage.stage_status?.status_key;
        const stageType = stage.type;
        const details = stage.default_version?.details || {};

        const key = CONTENT_DETAIL_KEY[stageType]?.[stageStatus];

        if (!key || !details[key]) return {};

        const detailsFormData = details[key]?.reduce((acc, group) => {
            const { order, type, details } = group;

            if (type === CONTENT_GROUP_TYPE.form) {
                return {
                    ...acc,
                    [order]: getInitialSettingsFormData({ settings: details?.inputs }),
                };
            }

            if (CONTENT_GROUP_TYPES_WITH_EDITING.includes(type)) {
                const key = CONTENT_GROUP_DETAIL_KEY[type];
                return {
                    ...acc,
                    [order]: (details?.[key] || []).map((item) => ({ ...item, state: 'default' })),
                };
            }

            return acc;
        }, {});

        return detailsFormData;
    };

    if (!cycleData || !formData) {
        return (
            <div className="page-position bg-white flex items-center justify-center">
                <Loading />
            </div>
        );
    }

    if (!cycleData?.current_stage) {
        return <Navigate to={backLinkHref} />;
    }

    const { current_stage } = cycleData;
    const { type, stage_status, default_version } = current_stage;

    const status = stage_status?.status_key;
    const details = default_version.details;

    const detailsKey = CONTENT_DETAIL_KEY[type]?.[status];
    const detailsKeyData = details?.[detailsKey] || [];

    const formatFormDataToSaveChangesRequest = async () => {
        const promises = detailsKeyData.map(async (group) => {
            const { type, order, details } = group;

            if (type === CONTENT_GROUP_TYPE.form) {
                const groupFormData = formData[order];
                const formDataWithHandledFiles = await replaceFilesInFormData(groupFormData);
                const formattedFormData = formatSettingsForRequestBody(formDataWithHandledFiles);

                const key = CONTENT_GROUP_DETAIL_KEY.form;
                return { ...group, details: { ...details, [key]: formattedFormData } };
            }

            if (CONTENT_GROUP_TYPES_WITH_EDITING.includes(type)) {
                const groupFormData = formData[order]?.map(
                    ({ state, errorMessage, ...item }) => item
                );
                const key = CONTENT_GROUP_DETAIL_KEY[type];
                return { ...group, details: { ...details, [key]: groupFormData } };
            }

            return group;
        });

        const formattedKeyData = await Promise.all(promises);
        return formattedKeyData;
    };

    const handleSaveStageChanges = async ({ throwOnError = false } = {}) => {
        try {
            const { isValid, updatedFormData, errorGroupsOrder } = checkIfReadyToSaveChanges({
                detailsKeyData,
                formData,
            });

            if (!isValid) {
                setFormData(updatedFormData);
                expandAllErrorGroups(errorGroupsOrder);
                setErrorAlert({ message: 'All fields should be valid.' });
                if (throwOnError) {
                    throw new Error('invalid data');
                }
                return;
            }

            if (!throwOnError) {
                setActionsLoading('saveChanges');
            }

            const formattedFormData = await formatFormDataToSaveChangesRequest();
            const requestBody = { details: { ...details, [detailsKey]: formattedFormData } };

            const { data: response } = await client.patch(
                API.ROUTES.cycle.cycleStage + current_stage.id + '/',
                requestBody
            );

            const formDataWithUploadedFiles = replaceUploadedFilesWithUrlsInFormData({
                formData,
                detailsKeyDataWithUploadedFiles: formattedFormData,
            });

            const sortedCurrentStage = sortStageContent(response);
            setCycleData((prevData) => ({ ...prevData, current_stage: sortedCurrentStage }));
            setFormData(formDataWithUploadedFiles);
            originalFormDataRef.current = formDataWithUploadedFiles;
            setAreChangesToSave(false);
        } catch (e) {
            if (throwOnError) {
                throw new Error('save changes error');
            }
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        } finally {
            if (!throwOnError) {
                setActionsLoading(null);
            }
        }
    };

    function replaceUploadedFilesWithUrlsInFormData({ formData, detailsKeyDataWithUploadedFiles }) {
        const updatedEntries = Object.entries(formData).map(([orderStr, groupFormData]) => {
            const order = +orderStr;

            const groupData = detailsKeyDataWithUploadedFiles?.find((item) => item.order === order);
            const groupFormDataWithUploadedFiles =
                groupData?.type === CONTENT_GROUP_TYPE.form &&
                groupData?.details?.[CONTENT_GROUP_DETAIL_KEY.form];

            if (!groupFormDataWithUploadedFiles?.length) return [order, groupFormData];

            const updatedGroup = groupFormData?.map((item) => {
                const { action, name } = item;
                if (
                    action === SETTING_ACTION_TYPE.single_input ||
                    action === SETTING_ACTION_TYPE.multi_file
                ) {
                    const uploadedValue = groupFormDataWithUploadedFiles.find(
                        (item) => item.name === name
                    )?.value;
                    if (uploadedValue) {
                        return { ...item, value: uploadedValue };
                    }
                }

                return item;
            });

            return [order, updatedGroup];
        });

        return Object.fromEntries(updatedEntries);
    }

    const handleReloadFormData = (stage) => {
        const formData = getInitialStageDetailsFormData(stage);

        setFormData(formData);
        originalFormDataRef.current = formData;
        setAreChangesToSave(false);
    };

    const submitStatusUpdate = async ({ status }) => {
        try {
            setActionsLoading('statusChange');

            if (areChangesToSave) {
                await handleSaveStageChanges({ throwOnError: true });
            }

            const { isValid, updatedFormData, errorGroupsOrder } = checkIfReadyToSubmit({
                detailsKeyData,
                formData,
            });

            if (!isValid) {
                setFormData(updatedFormData);
                originalFormDataRef.current = updatedFormData;
                setAreChangesToSave(false);
                expandAllErrorGroups(errorGroupsOrder);
                setErrorAlert({ message: 'All required fields should be filled and valid.' });
                return;
            }

            const { data } = await client.patch(
                API.ROUTES.cycle.cycleStage + current_stage.id + '/',
                { stage_status: status }
            );

            const sortedStageData = sortStageContent(data);
            setCycleData((prev) => ({ ...prev, current_stage: sortedStageData }));
            handleReloadFormData(sortedStageData);
        } catch (e) {
            console.log(e);
            setErrorAlert({ message: defaultErrorMessage });
        } finally {
            setActionsLoading(null);
        }
    };

    function expandAllErrorGroups(errorGroupsOrder) {
        const expandedGroups = {};

        errorGroupsOrder.forEach((order) => {
            expandedGroups[order] = true;
        });

        setExpandedGroups(expandedGroups);
    }

    const saveChangesLoading = actionsLoading === 'saveChanges';
    const saveChangesButtonState = saveChangesLoading
        ? 'loading'
        : areChangesToSave
        ? 'default'
        : 'disabled';

    return (
        <div className="page-position flex">
            <CycleMainPanel
                cycleData={cycleData}
                formData={formData}
                setFormData={setFormData}
                handleReloadFormData={handleReloadFormData}
                handleSaveStageChanges={handleSaveStageChanges}
                saveChangesButtonState={saveChangesButtonState}
                expandedGroups={expandedGroups}
                setExpandedGroups={setExpandedGroups}
                backLinkHref={backLinkHref}
            />

            {isDesktop && (
                <CycleSidePanel
                    cycleData={cycleData}
                    submitStatusUpdate={submitStatusUpdate}
                    actionsLoading={actionsLoading}
                />
            )}

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </div>
    );
};

export default CraftCycleDetailPage;
